import type { Plugin } from '@sharefe/share3d-engine'
import type { ExamplePresetKey } from './catalog'

export interface ExampleGraphicsSubject {
  readonly name: string
  readonly backends: readonly string[]
  readonly capabilities: readonly string[]
  /** 尚未声明 requirements 的主体按「未知」呈现，不能当作已兼容。 */
  readonly declared: boolean
}

export interface ExampleRouteGraphicsDescription {
  readonly presetKey: ExamplePresetKey
  readonly extras: readonly string[]
  readonly subjects: readonly ExampleGraphicsSubject[]
}

/** 引擎未在公共入口导出 requirements 类型，这里用结构化描述读取插件声明。 */
interface PluginRequirementsLike {
  readonly backends?: readonly string[]
  readonly capabilities?: readonly string[]
}

const PRESET_LABELS: Record<ExamplePresetKey, string> = {
  pointCloudOnly: 'presets.pointCloudOnly()',
  pointCloudAndGSplat: 'presets.pointCloudAndGSplat()',
  cadOnly: 'presets.cadOnly()',
  cadFixture: 'example 最小 CAD fixture',
  coreMesh: 'corePlugins() + MeshPlugin',
  none: '无基础 preset（route 自建插件集）',
}

// route 在 preset 之外追加的插件按类名惰性解析，避免 gallery 静态引入全部插件链。
const EXTRA_PLUGIN_FACTORIES: Record<string, () => Promise<Plugin | null>> = {
  AnimationPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.AnimationPlugin()
  },
  SpatialQueryPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.SpatialQueryPlugin()
  },
  CharacterPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.CharacterPlugin()
  },
  ClipPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.ClipPlugin()
  },
  ClipOverlayPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.ClipOverlayPlugin()
  },
  MeasurePlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.MeasurePlugin()
  },
  MeasureOverlayPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.MeasureOverlayPlugin()
  },
  GaussianSplatPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.GaussianSplatPlugin()
  },
  TilesPlugin: async () => {
    const engine = await import('@sharefe/share3d-engine')
    return new engine.TilesPlugin({
      viewIds: 'all',
      contentAdapters: [engine.createMeshContentAdapter()],
      source: new engine.HttpAssetSource(),
    })
  },
}

export function describePresetKey(presetKey: ExamplePresetKey): string {
  return PRESET_LABELS[presetKey]
}

/** 惰性解析 preset 与 route 追加插件；保持与 route 页面创建引擎时相同的插件来源。 */
export async function resolveExamplePresetPlugins(
  presetKey: ExamplePresetKey,
  extras: readonly string[] = []
): Promise<Plugin[]> {
  const engine = await import('@sharefe/share3d-engine')
  let plugins: Plugin[]
  switch (presetKey) {
    case 'pointCloudOnly':
      plugins = engine.presets.pointCloudOnly()
      break
    case 'pointCloudAndGSplat':
      plugins = engine.presets.pointCloudAndGSplat()
      break
    case 'cadOnly':
      plugins = engine.presets.cadOnly()
      break
    case 'cadFixture':
      plugins = (await import('../utils/cadFixturePlugins')).createCadFixturePlugins()
      break
    case 'coreMesh':
      plugins = [new engine.MeshPlugin()]
      break
    case 'none':
      plugins = []
      break
  }
  for (const label of extras) {
    const factory = EXTRA_PLUGIN_FACTORIES[label]
    if (!factory) continue
    const extra = await factory()
    if (extra) plugins.push(extra)
  }
  return plugins
}

function toSubject(plugin: Plugin): ExampleGraphicsSubject {
  const requirements = (plugin as Plugin & { graphicsRequirements?: PluginRequirementsLike })
    .graphicsRequirements
  return {
    name: plugin.name,
    backends: requirements?.backends ?? [],
    capabilities: requirements?.capabilities ?? [],
    declared: Boolean(requirements),
  }
}

/** 生成控制卡需要的静态主体清单（不创建 renderer/device，也不启动插件）。 */
export async function describeExampleRouteGraphics(
  presetKey: ExamplePresetKey,
  extras: readonly string[] = []
): Promise<ExampleRouteGraphicsDescription> {
  const plugins = await resolveExamplePresetPlugins(presetKey, extras)
  return {
    presetKey,
    extras,
    subjects: plugins.map(toSubject),
  }
}
